import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  TrackingSettingsResponseDto,
  VehicleAbsDto,
  VehicleBcmDto,
  VehicleDto,
  VehicleEcuDto,
  VehicleEnvironmentDto,
  VehicleMetricsResponseDto,
  VehicleParamsDto,
  VehicleTpmsDto,
} from '@fleet-beat/api';
import {
  AuthHeaders,
  BaseApiService,
  CoreInjectorTokens,
  Environment,
  GetManyDto,
  GetManyV2Dto,
  GetOneDto,
  GetOneV2Dto,
  PageableListDto,
  PageableListV2Dto,
  ResponseDto,
} from '@fleet-beat/core';
import { Observable, catchError, map } from 'rxjs';

export type VehicleMetricsTypes =
  | VehicleAbsDto
  | VehicleEcuDto
  | VehicleBcmDto
  | VehicleTpmsDto
  | VehicleEnvironmentDto;

@Injectable({
  providedIn: 'root',
})
export class TrucksApiService {
  private readonly baseUrl: string = `${this.environment.apiEndpoint}/trucks`;

  constructor(
    @Inject(CoreInjectorTokens.Environment) private environment: Environment,
    private baseApiService: BaseApiService,
  ) {}

  getMany(data: GetManyDto, auth: AuthHeaders): Observable<PageableListDto<VehicleDto>> {
    return this.baseApiService.get(this.baseUrl, { auth, params: data }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<PageableListDto<VehicleDto>>) => this.baseApiService.mapResponse(response)),
    );
  }

  getManyV2(data: GetManyV2Dto, auth: AuthHeaders): Observable<PageableListV2Dto<VehicleDto>> {
    return this.baseApiService.get(this.baseUrl, { auth, params: data }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<VehicleDto[]>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<VehicleDto[]>) => {
        return {
          data: response.data.map((truck: VehicleDto) => {
            return {
              ...truck,
              enable: truck.ovmsStatus === 'active' ? true : false,
            };
          }),
          ...response.meta,
        };
      }),
    );
  }

  getOne(id: string, auth: AuthHeaders, params?: GetOneV2Dto): Observable<VehicleDto> {
    return this.baseApiService.get(`${this.baseUrl}/${id}`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<VehicleDto>) => this.baseApiService.mapResponse(response)),
      map((response: VehicleDto) => {
        return {
          ...response,
          driverName: response?.drivers?.[0]?.fullName,
        };
      }),
    );
  }

  getParameters(id: string, auth: AuthHeaders, params?: GetOneDto): Observable<VehicleMetricsResponseDto[]> {
    return this.baseApiService.get(`${this.baseUrl}/${id}/parameters`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<VehicleMetricsResponseDto[]>) => this.baseApiService.mapResponse(response)),
    );
  }

  getLatestParametersByIds(body: { ids: string[] }, auth: AuthHeaders): Observable<VehicleParamsDto[]> {
    return this.baseApiService.post(`${this.baseUrl}/params/get-latest-by-ids`, body, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<VehicleParamsDto[]>) => this.baseApiService.mapResponse(response)),
    );
  }

  delete(id: string, auth: AuthHeaders): Observable<VehicleDto> {
    return this.baseApiService.delete(`${this.baseUrl}/${id}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<VehicleDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  addTruck(data: VehicleDto | VehicleDto, auth: AuthHeaders): Observable<VehicleDto> {
    return this.baseApiService.post(this.baseUrl, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<VehicleDto>) => this.baseApiService.mapResponse(response)),
    );
  }
  updateTruck(auth: AuthHeaders, payload: VehicleDto, id: string): Observable<VehicleDto> {
    return this.baseApiService.patch(`${this.baseUrl}/${id}`, payload, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<VehicleDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  updateTrackingSettings(id: string, data: VehicleDto, auth: AuthHeaders): Observable<TrackingSettingsResponseDto> {
    return this.baseApiService.patch(`${this.baseUrl}/${id}/tracking-settings`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<TrackingSettingsResponseDto>) => this.baseApiService.mapResponse(response)),
    );
  }
}
